// Native chat Spec 01 — the live feed + member compose (wireframes A–C).
//
// One river, four voices: native member (silver glyph), host/cohost (oxblood
// name + role tag), relayed external (plain), superchat (green — Spec 01,
// untouched). The unified-feed rule holds: the glyph marks a relationship, not a
// platform. Compose is server-verified through useSocialChat.send (optimistic →
// confirmed on the server echo; failed → inline retry; 429 → cooldown; muted →
// disabled). ChatFeed is shared with the Host Controls panel (frame D).

import { useRoomContext } from '@livekit/components-react';
import { Fragment, useEffect, useRef, useState } from 'react';

import {
  useSocialChat,
  type FeedChat,
  type FeedEntry,
  type UseSocialChatResult,
} from './useSocialChat';

import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils/cn';

const MAX = 280;
const COUNTER_AT = 200;

/** Silver half-moon ring — native ArcheTelos member mark (a relationship, not a
 *  platform badge). */
function MemberGlyph() {
  return (
    <span
      aria-hidden
      className="mr-1 inline-block h-[9px] w-[9px] rounded-full border border-silver align-middle"
      style={{ background: 'linear-gradient(135deg, currentColor 50%, transparent 50%)' }}
    />
  );
}

function HostTag({ role }: { role: 'host' | 'cohost' }) {
  return (
    <span className="ml-1.5 rounded-sm bg-oxblood px-1.5 py-px align-middle font-sans text-[8px] font-semibold uppercase tracking-[0.1em] text-white">
      {role}
    </span>
  );
}

export function ChatRow({
  m,
  mine,
  onRetry,
  onDismiss,
}: {
  m: FeedChat;
  mine: boolean;
  onRetry?: (nonce: string) => void;
  onDismiss?: (nonce: string) => void;
}) {
  const native = m.platform === 'archetelos';
  const isHost = m.role === 'host' || m.role === 'cohost';
  return (
    <li className={cn('font-sans text-sm leading-snug', m.pending === 'sending' && 'opacity-45')}>
      <span className="text-silver">
        {native && <MemberGlyph />}
        <span className={cn('font-medium', isHost ? 'text-oxblood-live' : 'text-video-text')}>
          {m.author.name}
        </span>
        {isHost && m.role ? <HostTag role={m.role} /> : null}{' '}
        <span className={mine ? 'text-video-text' : 'text-video-text-muted'}>{m.text}</span>
      </span>
      {m.pending === 'failed' && (
        <span className="mt-0.5 block font-sans text-[10px] text-oxblood-live">
          {m.failReason === 'muted' ? (
            "You've been muted by a host"
          ) : m.failReason === 'rate_limited' ? (
            'Sending too fast'
          ) : (
            <>
              {m.nonce && onRetry ? (
                <button type="button" onClick={() => onRetry(m.nonce!)} className="mr-2 underline">
                  Retry
                </button>
              ) : null}
              {m.nonce && onDismiss ? (
                <button
                  type="button"
                  onClick={() => onDismiss(m.nonce!)}
                  className="mr-2 underline"
                >
                  Dismiss
                </button>
              ) : null}
              Couldn&apos;t send
            </>
          )}
        </span>
      )}
    </li>
  );
}

function SuperchatRow({ sc }: { sc: Extract<FeedEntry, { kind: 'superchat' }> }) {
  return (
    <li className="rounded-sm border-l-2 border-superchat bg-superchat/10 py-1.5 pl-2 pr-2 font-sans text-sm leading-snug">
      <span className="font-semibold text-superchat">{sc.amount.display}</span>{' '}
      <span className="font-medium text-video-text">{sc.author.name}</span>
      {sc.text ? <span className="text-video-text-muted"> {sc.text}</span> : null}
    </li>
  );
}

/** Shared feed: renders the message river with the LIVE divider after the
 *  backfilled block. `renderRow` lets the host panel wrap native rows with
 *  moderation controls. */
export function ChatFeed({
  messages,
  backfillCount,
  myUserId,
  onRetry,
  onDismiss,
  renderChatRow,
}: {
  messages: FeedEntry[];
  backfillCount: number;
  myUserId?: string;
  onRetry?: (nonce: string) => void;
  onDismiss?: (nonce: string) => void;
  renderChatRow?: (m: FeedChat, mine: boolean) => React.ReactNode;
}) {
  if (messages.length === 0) {
    return <p className="px-4 py-3 font-sans text-xs text-video-text-faint">No messages yet.</p>;
  }
  const hasLiveAfterBackfill = backfillCount > 0 && messages.length > backfillCount;
  const nodes: React.ReactNode[] = [];
  messages.forEach((m, i) => {
    if (hasLiveAfterBackfill && i === backfillCount) {
      nodes.push(
        <li
          key="live-divider"
          className="my-2 border-b border-video-line text-center font-sans text-[9px] uppercase leading-[0.1] tracking-[0.18em] text-video-text-faint"
        >
          <span className="bg-video-inset px-2">Live</span>
        </li>,
      );
    }
    if (m.kind === 'superchat') {
      nodes.push(<SuperchatRow key={m.id} sc={m} />);
    } else if (renderChatRow) {
      nodes.push(<Fragment key={m.id}>{renderChatRow(m, m.senderId === myUserId)}</Fragment>);
    } else {
      nodes.push(
        <ChatRow
          key={m.id}
          m={m}
          mine={m.senderId === myUserId}
          onRetry={onRetry}
          onDismiss={onDismiss}
        />,
      );
    }
  });
  return <ul className="flex-1 space-y-2 overflow-y-auto px-4 py-3">{nodes}</ul>;
}

/** Compose row — shared by viewer + host (label differs). */
export function ChatCompose({
  onSend,
  muted,
  cooldownUntil,
  label,
}: {
  onSend: (text: string) => void;
  muted: boolean;
  cooldownUntil: number | null;
  label: React.ReactNode;
}) {
  const [text, setText] = useState('');
  const [now, setNow] = useState(() => Date.now());
  const taRef = useRef<HTMLTextAreaElement>(null);

  const cooling = cooldownUntil !== null && now < cooldownUntil;
  useEffect(() => {
    if (!cooling) return;
    const t = setInterval(() => setNow(Date.now()), 400);
    return () => clearInterval(t);
  }, [cooling]);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || muted || cooling) return;
    onSend(trimmed);
    setText('');
  };

  if (muted) {
    return (
      <div className="border-t border-video-line px-3 py-3">
        <div className="rounded-md border border-dashed border-video-line bg-video-bg px-3 py-2 font-sans text-xs text-video-text-faint">
          You can&apos;t send messages in this stream
        </div>
        <p className="mt-1.5 font-sans text-[10px] text-video-text-faint">Muted by a host</p>
      </div>
    );
  }

  return (
    <div className="border-t border-video-line px-3 py-2.5">
      <div className="flex items-end gap-2">
        <textarea
          ref={taRef}
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, MAX))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          rows={1}
          placeholder="Say something…"
          className="max-h-24 min-h-[34px] flex-1 resize-none rounded-md border border-video-line bg-video-bg px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!text.trim() || cooling}
          aria-label="Send"
          className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md bg-oxblood text-white disabled:opacity-40"
        >
          ➤
        </button>
      </div>
      <div className="mt-1 flex items-center justify-between font-sans text-[9px] text-video-text-faint">
        <span>{label}</span>
        {text.length >= COUNTER_AT && (
          <span>
            {text.length} / {MAX}
          </span>
        )}
      </div>
      {cooling && (
        <p className="mt-1 font-sans text-[10px] text-[#C9A227]">
          Slow down — you can send again in {Math.ceil((cooldownUntil! - now) / 1000)}s
        </p>
      )}
    </div>
  );
}

export function SocialChatPanel() {
  const room = useRoomContext();
  const { session } = useAuth();
  const chat: UseSocialChatResult = useSocialChat(room);

  return (
    <aside className="flex h-full flex-col bg-video-inset">
      <p className="border-b border-video-line px-4 py-3 font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
        Live chat
      </p>
      <ChatFeed
        messages={chat.messages}
        backfillCount={chat.backfillCount}
        myUserId={session?.user.id}
        onRetry={chat.retry}
        onDismiss={chat.dismiss}
      />
      {session && (
        <ChatCompose
          onSend={chat.send}
          muted={chat.muted}
          cooldownUntil={chat.cooldownUntil}
          label="Member"
        />
      )}
    </aside>
  );
}
